import merge from 'lodash/merge';

import {createSlice, PayloadAction, createSelector} from '@reduxjs/toolkit';
import {ISearchRefinements} from 'coreClasses/search/ISearch';
import {DEFAULT_SEARCH_PARAMS, REFINEMENT_DISALLOW_LIST} from 'brand/app/constants';
import type {RootState} from '../../types';
import type {ProductSearchResultState} from './interface';

const calcPageNumber = (offset: number, limit: number) => Math.floor(offset / limit) + 1 || 1;

const productSearchResultSelector = (state: RootState) => state.productSearchResult;

export const productSearchResultSlice = createSlice({
	name: 'productSearchResult',
	initialState: {},
	reducers: {
		receiveProductSearchResult: (
			state: ProductSearchResultState,
			action: PayloadAction<{
				productSearchResult: ProductSearchResultState,
				userSelectedRefinements: ISearchRefinements,
				key: string,
				queryKey: string,
				gridSize: number,
				limit?: number,
			}>
		) => {
			const key = action.payload.key;
			const queryKey = action.payload.queryKey;
			const productSearchResult = action.payload.productSearchResult;
			const limit = action.payload.limit ?? DEFAULT_SEARCH_PARAMS.limit;
			const offset = productSearchResult.offset ?? 0;
			const pageNumber = calcPageNumber(offset, limit);
			const gridSize = action.payload.gridSize;
			const refinements = productSearchResult?.refinements;
			const urlApplicableSelectedRefinements =
					Object.entries(productSearchResult?.selectedRefinements || {})
						.reduce((acc, [key, value]) => {
							if (REFINEMENT_DISALLOW_LIST.indexOf(key) === -1 && value) {
								acc[key] = value;
							}

							return acc;
						}, {});

			if (key in state && state[key].queryKey !== queryKey) {
				delete state[key];
			}

			state = merge(state, {
				[key]: {
					queryKey: queryKey,
					pages: {
						[pageNumber]: productSearchResult.hits,
					},
					...productSearchResult,
					urlApplicableSelectedRefinements,
					refinements,
					hits: undefined,
					gridSize: gridSize,
				},
			});

			if (state[key].hasOwnProperty('refinements')) {
				state[key] = {...state[key], refinements};
			}
		},
		storeGridSize: (
			state: ProductSearchResultState,
			action: PayloadAction<{
				key: string,
				gridSize: number,
			}>
		) => {
			const key = action.payload.key;
			const gridSize = action.payload.gridSize;

			state = merge(state, {
				[key]: {
					...state[key],
					gridSize,
				},
			});
		},
		flushProductSearchResult: (state: ProductSearchResultState, action: PayloadAction<string>) => {
			delete state[action.payload];
		},
		setUserSelectedRefinements: (
			state: ProductSearchResultState,
			action: PayloadAction<{
				key: string,
				userSelectedRefinements: ISearchRefinements,
			}>
		) => {
			const key = action.payload.key;
			const userSelectedRefinements = action.payload.userSelectedRefinements;

			if (key in state) {
				delete state[key].userSelectedRefinements;
			}

			state = merge(state, {
				[key]: {
					...state[key],
					userSelectedRefinements: {...userSelectedRefinements},
				},
			});
		},
		setUserSelectedSorting: (
			state: ProductSearchResultState,
			action: PayloadAction<{
				key: string,
				userSelectedSorting: string
			}>
		) => {
			const key = action.payload.key;
			const userSelectedSorting = action.payload.userSelectedSorting;

			state = merge(state, {
				[key]: {
					...state[key],
					userSelectedSorting: userSelectedSorting,
				},
			});
		},
	},
});

export const {
	receiveProductSearchResult,
	flushProductSearchResult,
	storeGridSize,
	setUserSelectedRefinements,
	setUserSelectedSorting,
} = productSearchResultSlice.actions;

export const selectProductSearchResult = createSelector(productSearchResultSelector, (productSearchResult) => {
	return Object.keys(productSearchResult).map((productSearchResultKey) => {
		return productSearchResult[productSearchResultKey];
	});
});

const selectKey = (state, key) => key;

const selectPageNumber = (state, key, pageNumber) => pageNumber;

export const selectProductSearchResultByKey = createSelector(
	[productSearchResultSelector, selectKey],
	(productSearchResult, key) => productSearchResult[key],
);

export const selectProductsHits = createSelector(
	[productSearchResultSelector, selectKey, selectPageNumber],
	(productSearchResult, key, pageNumber) => {
		return {
			queryKey: productSearchResult[key].queryKey,
			results: productSearchResult[key]?.pages && productSearchResult[key]?.pages[pageNumber]
				? productSearchResult[key].pages[pageNumber]
				: []
		};
	}
);

export default productSearchResultSlice.reducer;
